import {
    ApiOutlined,
    AuditOutlined,
    BellOutlined,
    BgColorsOutlined,
    CalendarOutlined,
    CopyOutlined,
    DesktopOutlined,
    ExportOutlined,
    FileDoneOutlined,
    FileTextOutlined,
    GroupOutlined,
    KeyOutlined,
    LaptopOutlined,
    SettingOutlined,
    StarOutlined,
    UnorderedListOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { AiFillSetting } from 'react-icons/ai';
import { BiFirstAid } from 'react-icons/bi';
import { FaRegHospital } from 'react-icons/fa';
import { HiOutlineDocumentReport } from 'react-icons/hi';
import { ImInsertTemplate } from 'react-icons/im';
import { RiDashboardLine } from 'react-icons/ri';
import { Pages } from '../../../../AppRouter';

/**
 * Üst menünün TEK kaynağı. Masaüstü (yatay) ve mobil (Drawer, dikey) menüler aynı diziyi
 * kullanır — eskiden iki ayrı JSX bloğu vardı ve her yeni sayfa iki yere ekleniyordu.
 *
 * Yetki: her madde `when` ile gizlenir. Alt menüler çocuğu kalmayınca tamamen düşer, bu yüzden
 * ayrıca "bu kullanıcının Sistem menüsü var mı" diye elde bir bayrak tutmaya gerek yok.
 *
 * Anahtarlar rota yolu olmalı: MainLayout tıklamada key'i `history.push`'a veriyor ve orta tık
 * işleyicisi data-menu-id içinde '/' arıyor. Grup/alt menü anahtarlarında '/' YOKTUR.
 */

// null/false çocukları eler; alt menü boş kaldıysa kendisini de eler.
const prune = (items) =>
    items
        .filter(Boolean)
        .map(item => {
            if (!item.children)
                return item;

            const children = prune(item.children);
            return children.length > 0 ? { ...item, children } : null;
        })
        .filter(Boolean);

const item = (when, key, icon, label) => (when ? { key, icon, label } : null);

// Başlıklı grup (antd type: 'group'). Çocuğu kalmayan grup prune ile düşer.
const group = (key, label, children) => ({ type: 'group', key, label, children });

/**
 * Açık olan rota için seçili menü anahtarını bulur.
 *
 * Düz `selectedKeys={[window.location.pathname]}` yetmiyordu: alt sayfaların (ör.
 * /app/study/123/requests) yolu hiçbir anahtara birebir eşit değil, o yüzden hiçbir madde
 * seçili görünmüyordu. Burada yol ÖN EKİNE bakıyoruz ve en UZUN eşleşme kazanıyor —
 * '/app/study' ile '/app/study-lists' karışmasın diye eşleşme yol segmenti sınırında aranır.
 * Grup/alt menü anahtarlarında '/' olmadığı için onlar doğal olarak elenir; üst maddeyi
 * antd zaten seçili çocuktan türetir.
 */
export function findSelectedKey(items, pathname) {
    let best = null;

    const walk = (list) => list.forEach(entry => {
        if (entry.children) {
            walk(entry.children);
            return;
        }

        const key = entry.key;
        if (typeof key !== 'string' || !key.includes('/'))
            return;

        if (pathname === key || pathname.startsWith(key + '/')) {
            if (!best || key.length > best.length)
                best = key;
        }
    });

    walk(items);
    return best;
}

export default function buildMenuItems({ t, hasPermission, showDashboard, PERMISSIONS }) {
    return prune([
        showDashboard() && { key: Pages.Dashboard.Home, icon: <RiDashboardLine />, label: t('Dashboard') },

        item(hasPermission(PERMISSIONS.STUDY_LIST), Pages.Study.Study, <BiFirstAid />, t('Study')),
        item(hasPermission(PERMISSIONS.REPORT_LIST), Pages.Report.DataGrid, <HiOutlineDocumentReport />, t('Reports')),

        // Menü artık faturalandırmanın yanında kalite/denetim maddesi de barındırdığı için
        // "Rapor Operasyonları" adını taşıyor.
        {
            key: 'report-operations',
            icon: <FileDoneOutlined />,
            label: t('Report Operations'),
            children: [
                item(hasPermission(PERMISSIONS.REPORT_BILLING_LIST), Pages.ReportBilling.DataGrid, <FileDoneOutlined />, t('Billing List')),
                item(hasPermission(PERMISSIONS.REPORT_BILLING_DUPLICATE_DETECT), Pages.ReportBilling.Duplicates, <CopyOutlined />, t('Duplicate Detection')),
                item(hasPermission(PERMISSIONS.RE_ASSESSMENT_MANAGE), Pages.ReportBilling.ReAssessment, <FileDoneOutlined />, t('Re-Assessment')),
                item(hasPermission(PERMISSIONS.CONTRACT_MANAGEMENT), Pages.Contract.Manage, <AuditOutlined />, t('Contracts')),
            ],
        },

        {
            key: 'worklist',
            icon: <UnorderedListOutlined />,
            label: t('Worklist'),
            children: [
                item(hasPermission(PERMISSIONS.WORKLIST_LIST), Pages.WorklistItem.DataGrid, <UnorderedListOutlined />, t('Worklist')),
                item(hasPermission(PERMISSIONS.WORKLIST_CREATE_EDIT), Pages.Appointment.Scheduler, <CalendarOutlined />, t('Appointments')),
            ],
        },

        // Sistem 17 maddeye çıktığı için konu başlıklarına bölündü. Başlıklar alt menü değil GRUP:
        // masaüstünde açılır pencere iki sütunlu ızgara olur (layout.css → .mega-menu-popup), her
        // grup başlığıyla birlikte tek bakışta görünür; mobil çekmecede başlıklı düz liste.
        {
            key: 'system',
            icon: <AiFillSetting />,
            label: t('System'),
            popupClassName: 'mega-menu-popup',
            children: [
                group('sys-definitions', t('Definitions'), [
                    item(hasPermission(PERMISSIONS.HOSPITAL_MANAGER), Pages.Hospital.Hospital, <FaRegHospital />, t('Hospital')),
                    item(hasPermission(PERMISSIONS.HOSPITAL_GROUP_MANAGER), Pages.HospitalGroup.HospitalGroupEdit, <FaRegHospital />, t('Hospital Groups')),
                    item(hasPermission(PERMISSIONS.DEVICE_LIST), Pages.Device.DataGrid, <LaptopOutlined />, t('Devices')),
                    item(hasPermission(PERMISSIONS.REQUEST_TYPE_MANAGER), Pages.RequestType.System, <GroupOutlined />, t('Request Types')),
                ]),
                group('sys-templates', t('Templates'), [
                    item(hasPermission(PERMISSIONS.REPORT_TEMPLATE_MANAGER), Pages.ReportTemplate.DataGrid, <ImInsertTemplate />, t('Report Templates')),
                    item(hasPermission(PERMISSIONS.REPORT_OUTPUT_TEMPLATE_MANAGER), Pages.ReportOutputTemplate.Builder, <ImInsertTemplate />, t('Report Output Templates')),
                ]),
                group('sys-users', t('User Management'), [
                    item(hasPermission(PERMISSIONS.USER_MANAGER), Pages.Role.Manager, <KeyOutlined />, t('Roles')),
                    item(hasPermission(PERMISSIONS.USER_MANAGER), Pages.User.DataGrid, <UserOutlined />, t('Users')),
                ]),
                group('sys-integrations', t('Integrations'), [
                    item(hasPermission(PERMISSIONS.HL7_MANAGER), Pages.HL7.DataGrid, <ApiOutlined />, t('HL7')),
                    item(hasPermission(PERMISSIONS.TELETIP_MANAGER), Pages.Teletip.DataGrid, <ApiOutlined />, t('Teletip')),
                    item(hasPermission(PERMISSIONS.AUTO_EXPORT_MANAGE), Pages.AutoExport.Rules, <ExportOutlined />, t('Auto Export Rules')),
                ]),
                group('sys-logs', t('Records & Notifications'), [
                    item(hasPermission(PERMISSIONS.LOG_MANAGEMENT), Pages.Log.DataGrid, <FileTextOutlined />, t('Logs')),
                    item(hasPermission(PERMISSIONS.LOG_MANAGEMENT), Pages.Log.AppLogs, <FileTextOutlined />, t('App Logs')),
                    item(hasPermission(PERMISSIONS.NOTIFICATION_MANAGE), Pages.Notification.Manage, <BellOutlined />, t('Notification Management')),
                ]),
                group('sys-settings', t('Settings'), [
                    item(hasPermission(PERMISSIONS.VIEWER_SETTINGS_MANAGER), Pages.ViewerSettings.Settings, <DesktopOutlined />, t('Viewer Settings')),
                    item(hasPermission(PERMISSIONS.BRANDING_MANAGE), Pages.Settings.Branding, <BgColorsOutlined />, t('Branding')),
                    item(hasPermission(PERMISSIONS.CONFIG_MANAGE), Pages.Settings.System, <SettingOutlined />, t('System')),
                ]),
                // Listelerim artık başlıkta değil, Sistem'in altında duruyor.
                group('sys-lists', t('Lists'), [
                    item(
                        hasPermission(PERMISSIONS.LIST_CREATE) || hasPermission(PERMISSIONS.LIST_VIEW) || hasPermission(PERMISSIONS.LIST_VIEW_ALL),
                        Pages.StudyList.Manage,
                        <StarOutlined />,
                        t('My Lists')
                    ),
                ]),
            ],
        },
    ]);
}
